/**
 * Analyzer: Day Flag Rollup
 *
 * Aggregates per-trade bar-context flags (from `services/barAnalysis/tradeFlags`)
 * into day-level observations. Where the flag chips on each trade card
 * surface signal at the trade grain, this analyzer tells the trader what
 * those signals MEAN in aggregate — "3 of today's losers were Wicked out"
 * is a different (and more actionable) message than the same fact spread
 * across three separate trade chips.
 *
 * Different from dayBarContext:
 *   - dayBarContext computes its own thresholds against barAnalysis to
 *     emit specific patterns (winner-reversed, MFE leakage, EMA stack).
 *   - dayFlagRollup consumes the per-trade flag layer and counts. The
 *     overlap is intentional — same source data, different rollup grain.
 *
 * Why both? Because the trade-flag chips have to fire on a single trade
 * (they ARE the trade-card signal), while the day patterns need different
 * thresholds to produce a useful day-level narrative. Letting both consume
 * the same `barAnalysis` keeps them aligned without forcing one to be a
 * derivation of the other.
 *
 * Reads:
 *   ctx.trades (today's trades)
 *   tradeFlags.computeTradeFlags(trade) → Array<{key,label,severity,hint}>
 *
 * Trades without barAnalysis contribute nothing — backfill is the
 * prerequisite, same as the chips themselves.
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';
import { computeTradeFlags } from '../../../barAnalysis/tradeFlags.js';

export const key = 'dayFlagRollup';
export const tier = 'free';

// Per-flag rollup thresholds. Higher bars for "good" flags because positive
// reinforcement at low signal is noise; for "warn" flags, two firings
// already point at a pattern worth naming.
const MIN_OCCURRENCES = {
  warn: 2,
  info: 2,
  good: 3,
};

// Per-flag-key narrative — pulled out of the inline render so the analyzer
// stays small and so the strings are easy to tune in one place.
//
// title()    builds the headline; gets { count, winners, losers } context
// detail     why this matters, said once per pattern
// action     one sharp recommendation that actually changes behavior next session
const FLAG_NARRATIVES = {
  'wicked-out': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    priority: 78,
    title: ({ count }) => `${count} losers wicked out before the move worked`,
    detail: 'Each was within 80%+ of target before reversing into a stop. The market was reaching toward your level, then kicking you out.',
    action: 'Loosen stops by 0.2-0.3R or move them to structural levels (prior swing, EMA reclaim) instead of fixed-tick distance.',
    defaultMistake: 'Stop Too Tight',
  },
  'full-risk': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    priority: 74,
    title: ({ count }) => `${count} losers went the full 1R to stop`,
    detail: 'Trades pushed all the way to the stop with little hesitation. Either the entry timing or the stop placement is leaving no margin.',
    action: 'Pull these trades up. Did the entry trigger fire too early, or was the stop placed inside normal noise? Adjust whichever is the real cause.',
    defaultMistake: 'Full Risk Taken',
  },
  'bag-held': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    priority: 72,
    title: ({ count }) => `${count} trades bag-held into losses`,
    detail: 'Crossed 50–80% of the way to target, then turned into losers. There was a scratch available on every one.',
    action: 'Define a scratch protocol — e.g., "if half-target hit then reverses, exit at BE or +1 tick." Saves real money on choppy days.',
    defaultMistake: 'No Scratch Protocol',
  },
  'mfe-leak': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    priority: 70,
    title: ({ count }) => `${count} winners capped well below the available move`,
    detail: 'Each captured under 30% of the favorable excursion. Targets are landing inside the profit zone, not at the edge of it.',
    action: 'Push T2/T3 further out, or convert one target into a runner with a structural trail. The market is offering more than you are asking.',
    defaultMistake: 'Took Profit Too Early',
  },
  'round-trip': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    priority: 68,
    title: ({ count }) => `${count} trades round-tripped — big MFE, kept little`,
    detail: 'Each reached 1.5R+ favorable but ended at 40% capture or below. This is the "let it run, then watched it die" pattern.',
    action: 'After the first runner target hits, set a structural trail (prior swing, last reclaim). Locks in 60-80% of these instead of giving them back.',
    defaultMistake: 'Held Through Reversal',
  },
  'counter-trend': {
    severity: SEVERITY.INFO,
    category: CATEGORY.SETUP,
    priority: 60,
    title: ({ count }) => `${count} counter-trend entries today`,
    detail: 'Entries against the EMA stack — different rules apply. If your playbook was meant to be trend-following, this is the question.',
    action: 'Was today a counter-trend day by design (range, exhaustion fade)? If yes, note the regime tell. If not, the playbook drifted.',
  },
  'vwap-against': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    priority: 64,
    title: ({ count }) => `${count} entries on the wrong side of VWAP`,
    detail: 'Entered with institutional flow opposed. VWAP-adverse entries underperform consistently across most futures strategies.',
    action: 'Add a checklist line: "Am I on VWAP\'s side?" Skip the entry when the answer is no.',
  },
  'sharp-entry': {
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.PROCESS,
    priority: 58,
    title: ({ count }) => `${count} sharp entries today — your timing was on`,
    detail: 'Each entered with under 20% of risk against you before the move worked. Clean reads.',
    action: 'Note the conditions that produced these in your Lessons box. Sharp-entry days have repeatable triggers — find them.',
  },
  'with-stack': {
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.SETUP,
    priority: 54,
    title: ({ count }) => `${count} entries aligned with the EMA stack`,
    detail: 'Stayed in the trend lane. With-stack entries carry significantly higher expectancy than counter-trend on most days.',
    action: 'Check whether your with-stack WR is meaningfully better than against — if yes, codify the alignment as a hard rule.',
  },
  'htf-aligned': {
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.SETUP,
    priority: 66,
    title: ({ count }) => `${count} entries aligned across higher timeframes`,
    detail: 'Each entry agreed with every higher-timeframe EMA stack snapshot we captured. This is the trend-with-trend context where edge concentrates.',
    action: 'Note these in Lessons. HTF-aligned days are repeatable — what was different about your read this morning vs days when alignment slipped?',
  },
  'htf-mixed': {
    severity: SEVERITY.INFO,
    category: CATEGORY.SETUP,
    priority: 50,
    title: ({ count }) => `${count} entries with split HTF context`,
    detail: 'Different higher timeframes disagreed on direction. These setups are explicitly counter-rotation plays — they need their own playbook.',
    action: 'If counter-rotation was your plan, name it in the thesis. If it wasn\'t, the lesson is to wait for HTFs to align.',
  },
  // Per-TF "against" rollups. Same shape, different tag — surface them
  // separately so the day-level message can name the specific timeframe.
  'against-1h': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    priority: 76,
    title: ({ count }) => `${count} entries fought the 1H trend`,
    detail: 'Each entry opposed the 1H EMA stack. Counter-1H trades have meaningfully lower expectancy unless this is a deliberate fade against exhaustion.',
    action: 'Either codify these as a "1H fade" playbook with stricter rules (exhaustion signal, structure-break confirmation) or filter them out by checklist.',
  },
  'against-4h': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    priority: 80,
    title: ({ count }) => `${count} entries fought the 4H trend`,
    detail: '4H is the heaviest weight in HTF context. Fighting it consistently — even when entries are otherwise clean — is the slow leak that compounds.',
    action: 'Add "Is the 4H stack with me?" as a hard checklist gate. Skip when no.',
  },
  'against-15m': {
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    priority: 70,
    title: ({ count }) => `${count} entries fought the 15M trend`,
    detail: '15M is the closest HTF to most entry frames — when the entry-TF stack flips but 15M hasn\'t yet, you\'re front-running structure.',
    action: 'Wait for the 15M stack to confirm your direction before pulling the trigger, or accept this is your scalping window with tighter targets.',
  },
};

const occurrencesFor = (severity) => MIN_OCCURRENCES[severity] ?? 2;

// Public analyze ──────────────────────────────────────────────────────────

export const analyze = (ctx) => {
  // Day-scope analyzers receive ctx.day (a DayContext) from the day scope
  // dispatcher. DayContext exposes enriched trades (with netPnl already
  // computed) as `day.trades`. Contract tests call analyze({ trades })
  // directly so accept either path.
  const trades = Array.isArray(ctx?.day?.trades) && ctx.day.trades.length > 0
    ? ctx.day.trades
    : (Array.isArray(ctx?.trades) ? ctx.trades : null);
  if (!trades || trades.length === 0) return null;
  const date = ctx?.day?.date || ctx?.date || null;

  // Walk every trade, gather flags. Each flag entry is enriched with the
  // trade reference so we can compute winner/loser breakdowns later.
  const byKey = new Map();
  for (const t of trades) {
    const flags = computeTradeFlags(t);
    if (!flags || flags.length === 0) continue;
    for (const f of flags) {
      if (!byKey.has(f.key)) byKey.set(f.key, { flag: f, trades: [] });
      byKey.get(f.key).trades.push(t);
    }
  }

  if (byKey.size === 0) return null;

  const observations = [];
  for (const [flagKey, { flag, trades: hits }] of byKey.entries()) {
    const narrative = FLAG_NARRATIVES[flagKey];
    if (!narrative) continue; // unknown flag — skip rather than emit a generic obs
    const minN = occurrencesFor(flag.severity);
    if (hits.length < minN) continue;

    const winners = hits.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length;
    const losers  = hits.filter(t => Number.isFinite(t.netPnl) && t.netPnl < 0).length;
    const titleCtx = { count: hits.length, winners, losers };
    const tradeIds = hits.map(t => t.id).filter(Boolean);

    // Build structured actions per narrative:
    //   - Tag mistake (when narrative defines a default mistake tag)
    //   - Jump to a representative trade (when there's at least one)
    const observationActions = [];
    if (narrative.defaultMistake && tradeIds.length > 0) {
      observationActions.push(makeAction({
        type: ACTION_TYPE.TAG_TRADE_MISTAKE,
        label: `Tag as "${narrative.defaultMistake}"`,
        payload: { tradeIds, mistake: narrative.defaultMistake },
        confirmText: `Tag ${tradeIds.length} trade${tradeIds.length > 1 ? 's' : ''} as "${narrative.defaultMistake}"?`,
        doneLabel: 'Tagged ✓',
      }));
    }
    // Jump to the first hit — useful for warn flags so the trader can see
    // a concrete example of the pattern. Skip for "good" rollups where
    // jumping isn't the user-intent (they're not investigating a problem).
    if (tradeIds.length > 0 && narrative.severity === SEVERITY.WARNING) {
      const firstWithDate = hits.find(t => t.date && t.id);
      if (firstWithDate) {
        observationActions.push(makeAction({
          type: ACTION_TYPE.JUMP_TO_TRADE,
          label: 'Show first',
          payload: { tradeId: firstWithDate.id, date: firstWithDate.date },
          doneLabel: 'Jumping…',
        }));
      }
    }

    observations.push(makeObservation({
      id: `${key}:${flagKey}`,
      analyzer: key,
      scope: 'day',
      scopeId: date,
      severity: narrative.severity,
      category: narrative.category,
      title: narrative.title(titleCtx),
      detail: narrative.detail,
      action: narrative.action,
      actions: observationActions.length > 0 ? observationActions : null,
      evidence: {
        flagKey,
        count: hits.length,
        winners,
        losers,
        tradeIds,
      },
      confidence: 0.78,
      priority: narrative.priority,
    }));
  }

  if (observations.length === 0) return null;
  return observations;
};

// Exports for tests
export { FLAG_NARRATIVES, MIN_OCCURRENCES };
